// Phase SA-4/4 — useBettingProfile hook.
//
// Reads the SA-2 user_betting_profile row directly so the Slip
// Autopsy v2 profile components can compose against a single
// source of truth. Distinct from the legacy useSlipAutopsy hook
// (which still owns autopsy submission + Edge fn invocation) — this
// is read-only and lightweight.
//
// Empty row is the happy path per N04: every consumer renders its
// own meaningful empty state instead of returning null.
//
// SA-6 (Edge fn upgrade) is the one that POPULATES these JSONB
// columns; until then they default to {} / [] / null in the DB,
// which is exactly what we want for empty states.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type ProfileConfidence = "low" | "building" | "strong" | "expert";
export type Archetype =
  | "value_seeker"
  | "jackpot_hunter"
  | "disciplined_compounder"
  | "intelligence_learner";

/** Per-market entry stored under user_betting_profile.market_performance.
 *
 * Shape:
 *   { <market_slug>: { legs, wins, rate, process_accuracy, strikza_rate? } }
 *
 * `rate` and `process_accuracy` are 0..1 decimals (NOT percentages).
 * `strikza_rate` is the STRIKZA model's win rate on the same market
 * — Pro-only display.
 */
export interface MarketPerformanceEntry {
  legs: number;
  wins: number;
  rate: number | null;
  process_accuracy: number | null;
  strikza_rate?: number | null;
}

/** Per-odds-band entry under user_betting_profile.odds_range_performance.
 *
 * Shape:
 *   { '1.20_1.50': { legs, wins, rate } }
 */
export interface OddsRangeEntry {
  legs: number;
  wins: number;
  rate: number | null;
  process_accuracy?: number | null;
}

/** Per-bookmaker entry under user_betting_profile.bookmaker_performance. */
export interface BookmakerEntry {
  legs: number;
  wins: number;
  rate: number | null;
  process_accuracy: number | null;
  clv_pct?: number | null;
}

export interface PatternAlert {
  key: string;
  headline: string;
  detail: string;
  dismissed?: boolean;
  learn_more_url?: string | null;
}

export interface BettingProfileFull {
  user_id: string | null;
  total_slips: number;
  total_legs: number;
  win_rate: number | null;
  process_accuracy_pct: number | null;
  archetype: Archetype | null;
  profile_confidence: ProfileConfidence;
  market_performance: Record<string, MarketPerformanceEntry>;
  odds_range_performance: Record<string, OddsRangeEntry>;
  bookmaker_performance: Record<string, BookmakerEntry>;
  pattern_alerts: PatternAlert[];
  last_updated: string | null;
}

const EMPTY_PROFILE: BettingProfileFull = {
  user_id: null,
  total_slips: 0,
  total_legs: 0,
  win_rate: null,
  process_accuracy_pct: null,
  archetype: null,
  profile_confidence: "low",
  market_performance: {},
  odds_range_performance: {},
  bookmaker_performance: {},
  pattern_alerts: [],
  last_updated: null,
};

/** Pure derivation: profile_confidence from total_slips per brief §03 A1. */
export function deriveConfidenceTier(totalSlips: number): ProfileConfidence {
  if (!Number.isFinite(totalSlips) || totalSlips <= 0) return "low";
  if (totalSlips <= 4) return "low";
  if (totalSlips <= 19) return "building";
  if (totalSlips <= 49) return "strong";
  return "expert";
}

/** Map slug → human label for the 4 brief-defined archetypes. */
export function archetypeLabel(a: Archetype | null): string {
  switch (a) {
    case "value_seeker":
      return "Value Seeker";
    case "jackpot_hunter":
      return "Jackpot Hunter";
    case "disciplined_compounder":
      return "Disciplined Compounder";
    case "intelligence_learner":
      return "Intelligence Learner";
    default:
      return "";
  }
}

export interface UseBettingProfileState {
  profile: BettingProfileFull;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBettingProfile(): UseBettingProfileState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [profile, setProfile] = useState<BettingProfileFull>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setProfile(EMPTY_PROFILE);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // SA-2: user_betting_profile has 7 new columns; db/types.ts
      // hasn't been regenerated yet so we use the `as never` cast
      // pattern established in useSlipAutopsy.
      const { data, error: fetchErr } = await supabase
        .from("user_betting_profile" as never)
        .select(
          // The table stores `slips_analyzed` (aliased to total_slips) and
          // `avg_legs_per_slip`; there is no total_legs column, so legs are
          // derived below. Selecting non-existent columns 400s the whole row.
          // eslint-disable-next-line max-len
          "user_id, total_slips:slips_analyzed, avg_legs_per_slip, win_rate, process_accuracy_pct, archetype, profile_confidence, market_performance, odds_range_performance, bookmaker_performance, pattern_alerts, last_updated",
        )
        .eq("user_id", user.id)
        .maybeSingle();

      if (fetchErr) {
        // Brief N01 spirit — never surface tech errors. Soft-fail
        // to the empty profile.
        // eslint-disable-next-line no-console
        console.warn("[useBettingProfile] fetch failed:", fetchErr.message);
        setProfile(EMPTY_PROFILE);
        setLoading(false);
        return;
      }

      if (!data) {
        setProfile(EMPTY_PROFILE);
        setLoading(false);
        return;
      }

      // Hydrate with sensible defaults — every field can be null
      // server-side until SA-6 populates the JSONBs.
      const row = data as unknown as Partial<BettingProfileFull> & {
        win_rate?: number | string | null;
        avg_legs_per_slip?: number | string | null;
      };

      const totalSlips = Number(row.total_slips ?? 0);
      // No total_legs column; reconstruct legs audited from the average.
      const totalLegs = Math.round(Number(row.avg_legs_per_slip ?? 0) * totalSlips);
      // The DB `profile_confidence` column may be 'low' until SA-6
      // writes more meaningful values; derive from total_slips to
      // keep the UX honest in the meantime.
      const derived = deriveConfidenceTier(totalSlips);
      const stored = row.profile_confidence as ProfileConfidence | null;

      setProfile({
        user_id: row.user_id ?? user.id,
        total_slips: totalSlips,
        total_legs: totalLegs,
        win_rate:
          row.win_rate == null ? null : Number(row.win_rate as number | string),
        process_accuracy_pct:
          row.process_accuracy_pct == null
            ? null
            : Number(row.process_accuracy_pct),
        archetype: (row.archetype as Archetype | null) ?? null,
        profile_confidence: stored ?? derived,
        market_performance:
          (row.market_performance as Record<string, MarketPerformanceEntry>) ??
          {},
        odds_range_performance:
          (row.odds_range_performance as Record<string, OddsRangeEntry>) ?? {},
        bookmaker_performance:
          (row.bookmaker_performance as Record<string, BookmakerEntry>) ?? {},
        pattern_alerts: (row.pattern_alerts as PatternAlert[]) ?? [],
        last_updated: row.last_updated ?? null,
      });
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "[useBettingProfile] threw:",
        e instanceof Error ? e.message : String(e),
      );
      setProfile(EMPTY_PROFILE);
      setLoading(false);
    }
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseBettingProfileState>(
    () => ({ profile, loading, error, refetch }),
    [profile, loading, error, refetch],
  );
}
